import { useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, Camera, Car, MapPin } from "lucide-react";
import { API } from "../lib/apiClient";
import { isValidPlate, normalizePlate, plateToProvince } from "../lib/plate";
import { Modal } from "./common";

export const CAR_BRANDS = [
  "Renault", "Fiat", "Ford", "Toyota", "Volkswagen", "Hyundai", "Peugeot", "Honda",
  "Opel", "Mercedes-Benz", "BMW", "Audi", "Dacia", "Citroen", "Skoda", "Nissan",
  "Kia", "Mazda", "Suzuki", "Volvo", "Mitsubishi", "Seat", "Chevrolet", "Subaru",
  "Tesla", "Cupra", "Land Rover", "Jaguar", "Porsche", "Mini", "Alfa Romeo", "Chery",
  "Togg", "MG", "BYD", "Isuzu", "Iveco",
];

export const CAR_MODELS_HINT = [
  "Megane", "Clio", "Symbol", "Kadjar", "Talisman", "Egea", "Doblo", "Panda", "500L", "Punto",
  "Focus", "Fiesta", "Kuga", "Mondeo", "Transit", "Corolla", "Yaris", "C-HR", "RAV4", "Auris",
  "Golf", "Passat", "Polo", "Tiguan", "Jetta", "i20", "i30", "Tucson", "Elantra", "Accent",
  "308", "3008", "2008", "301", "5008", "Civic", "City", "CR-V", "Jazz", "Insignia",
  "Astra", "Corsa", "Mokka", "C-Class", "E-Class", "A-Class", "GLA", "GLC", "3 Serisi", "5 Serisi",
  "X1", "X3", "A3", "A4", "Q3", "Q5", "Sandero", "Duster", "Logan", "C3", "C4",
  "Octavia", "Fabia", "Rapid", "Qashqai", "Juke", "Micra", "Sportage", "Ceed", "Rio",
];

export function VehicleModal({ initial, customers, onSave, onClose }) {
  const [form, setForm] = useState(initial || { plate: "", customer: "", brand: "", model: "", year: "", note: "", ruhsatSeriNo: "", inspectionLast: "", inspectionEnd: "" });
  const [busy, setBusy] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrMsg, setOcrMsg] = useState("");
  const fileInputRef = useRef(null);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      if (name === "plate") return { ...f, plate: value.toUpperCase() };
      if (name === "ruhsatSeriNo") return { ...f, ruhsatSeriNo: value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8) };
      return { ...f, [name]: value };
    });
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!normalizePlate(form.plate)) { window.alert("Plaka gerekli"); return; }
    if (form.ruhsatSeriNo && !/^[A-Z]{2}\d{6}$/.test(form.ruhsatSeriNo)) {
      window.alert("Ruhsat Seri No formatı: 2 harf + 6 rakam (örn. IJ035491)"); return;
    }
    setBusy(true);
    await onSave({ ...form, plate: normalizePlate(form.plate) });
    setBusy(false);
  };

  const runOcr = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setOcrMsg("Lütfen bir görsel dosya seçin."); return; }
    if (file.size > 10 * 1024 * 1024) { setOcrMsg("Görsel 10MB'tan büyük olamaz."); return; }
    setOcrBusy(true);
    setOcrMsg("Ruhsat okunuyor…");
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await axios.post(`${API}/vehicles/ocr-ruhsat`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Fill only empty fields, don't override user input
      setForm((f) => ({
        ...f,
        plate: f.plate || data.plate || "",
        brand: f.brand || data.brand || "",
        model: f.model || data.model || "",
        year: f.year || data.year || "",
        ruhsatSeriNo: f.ruhsatSeriNo || data.ruhsatSeriNo || "",
      }));
      const found = [data.plate, data.brand, data.model, data.year, data.ruhsatSeriNo].filter(Boolean).length;
      setOcrMsg(found ? `✓ ${found} alan otomatik dolduruldu · gerekirse düzeltebilirsiniz` : "Ruhsat okunamadı, lütfen elle girin.");
    } catch (err) {
      setOcrMsg(err?.response?.data?.detail || "Ruhsat okunamadı");
    } finally {
      setOcrBusy(false);
      setTimeout(() => setOcrMsg(""), 6000);
    }
  };

  const province = plateToProvince(form.plate);

  return (
    <Modal title={initial?.id ? "Aracı düzenle" : "Yeni araç ekle"} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="ocr-banner">
          <div className="ocr-banner-body">
            <strong><Camera size={14} /> Ruhsat fotoğrafı ile hızlı doldur</strong>
            <span>Ruhsatı çekin veya galeriden seçin — marka, model ve plaka AI ile otomatik doldurulur.</span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            style={{ display: "none" }}
            data-testid="ruhsat-file-input"
            onChange={(e) => runOcr(e.target.files?.[0])}
          />
          <button type="button" className="primary-button" data-testid="ruhsat-scan-button" disabled={ocrBusy} onClick={() => fileInputRef.current?.click()}>
            {ocrBusy ? "Okunuyor…" : <><Camera size={14} /> Ruhsat tara</>}
          </button>
        </div>
        {ocrMsg && <div className="ocr-msg" data-testid="ocr-msg">{ocrMsg}</div>}

        <div className="form-grid">
          <label className="field">
            <span>Plaka *</span>
            <input data-testid="vehicle-plate-input" name="plate" value={form.plate} onChange={change} placeholder="34 ABC 123" required />
            {form.plate && !isValidPlate(form.plate) ? (
              <em className="field-hint plate-warning"><AlertTriangle size={11} /> Standart TR plaka değil (ör: 34 ABC 123). Yine de kaydedebilirsiniz.</em>
            ) : province ? (
              <em className="field-hint" data-testid="plate-province"><MapPin size={11} /> {province} · Bu plaka bu ilde kayıtlı</em>
            ) : (
              <em className="field-hint">Poliçesiz araç kaydı. Diğer alanlar isteğe bağlı.</em>
            )}
          </label>
          <label className="field">
            <span>Ruhsat Seri No</span>
            <input
              data-testid="vehicle-ruhsat-input"
              name="ruhsatSeriNo"
              value={form.ruhsatSeriNo || ""}
              onChange={change}
              placeholder="IJ035491"
              maxLength={8}
              autoComplete="off"
            />
            {form.ruhsatSeriNo && !/^[A-Z]{2}\d{6}$/.test(form.ruhsatSeriNo) ? (
              <em className="field-hint plate-warning"><AlertTriangle size={11} /> 2 harf + 6 rakam olmalı (örn. IJ035491)</em>
            ) : (
              <em className="field-hint">2 harf + 6 rakam · opsiyonel</em>
            )}
          </label>
          <label className="field full">
            <span>Sahibi / Müşteri</span>
            <input
              data-testid="vehicle-customer-input"
              list="vehicle-customer-datalist"
              name="customer"
              value={form.customer}
              onChange={change}
              placeholder="Müşteri seçin veya yeni isim yazın (opsiyonel)"
            />
            <datalist id="vehicle-customer-datalist">
              {customers.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
          </label>
          <label className="field">
            <span>Marka</span>
            <input data-testid="vehicle-brand-input" name="brand" list="brand-datalist" value={form.brand} onChange={change} placeholder="Renault, BMW…" />
            <datalist id="brand-datalist">
              {CAR_BRANDS.map((b) => <option key={b} value={b} />)}
            </datalist>
          </label>
          <label className="field">
            <span>Model</span>
            <input data-testid="vehicle-model-input" name="model" list="model-datalist" value={form.model} onChange={change} placeholder="Megane, 320i…" />
            <datalist id="model-datalist">
              {CAR_MODELS_HINT.map((m) => <option key={m} value={m} />)}
            </datalist>
          </label>
          <label className="field">
            <span>Model Yılı</span>
            <input data-testid="vehicle-year-input" name="year" value={form.year} onChange={change} placeholder="2020" />
          </label>
          <label className="field">
            <span>Son muayene tarihi</span>
            <input data-testid="vehicle-inspection-last" name="inspectionLast" type="date" value={form.inspectionLast || ""} onChange={change} />
          </label>
          <label className="field">
            <span>Bir sonraki muayene</span>
            <input data-testid="vehicle-inspection-end" name="inspectionEnd" type="date" value={form.inspectionEnd || ""} onChange={change} />
          </label>
          <label className="field">
            <span>Not</span>
            <input data-testid="vehicle-note-input" name="note" value={form.note} onChange={change} placeholder="Renk, kilometre vb." />
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="outline-button" onClick={onClose}>Vazgeç</button>
          <button type="submit" className="primary-button" data-testid="vehicle-save" disabled={busy}><Car size={15} /> {busy ? "Kaydediliyor…" : (initial?.id ? "Güncelle" : "Aracı kaydet")}</button>
        </div>
      </form>
    </Modal>
  );
}
